/* eslint-disable react/self-closing-comp */
import { Attributes } from '@leverege/ui-attributes'
import { Flex, Text } from '@leverege/ui-elements'
import { AxisBottom, AxisLeft } from '@visx/axis'
import { localPoint } from '@visx/event'
import { Group } from '@visx/group'
import { LegendOrdinal } from '@visx/legend'
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale'
import { Bar, BarStack } from '@visx/shape'
import { TooltipWithBounds, defaultStyles, withTooltip } from '@visx/tooltip'
import React, { useMemo } from 'react'

import Constants from '../../../../util/Constants'
import useBarData from './hooks/useBarData'

const { GRAPH_HEIGHT } = Constants

const BACKGROUND = '#fbfbfd'
const VERTICAL_MARGIN = 80
const HORIZONTAL_MARGIN = 50
const tooltipStyles = {
  ...defaultStyles
}

const getHour = d => d.hour
const getCount = d => d.count
const formatTime = ( d ) => {
  const hour = parseInt( d )

  if ( hour === 24 ) {
    return '12 am'
  }

  if ( hour < 12 ) {
    return `${hour} am`
  }

  if ( hour === 12 ) {
    return `${hour} pm`
  }

  return `${hour - 12} pm`
}

function UsageBarChart( props ) {
  const {
    width,
    data : pData,
    stations,
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip,
    showLegend = true,
    numXTicks,
    verticalMargin = VERTICAL_MARGIN,
    horizontalMargin = HORIZONTAL_MARGIN
  } = props

  const { colorScale, legendColorScale, stationNames } = useMemo( () => {
    const stationNames = stations
      .sort( ( stationA, stationB ) => {
        let numberA = Attributes.get( 'station.number', stationA )
        let numberB = Attributes.get( 'station.number', stationB )

        numberA = numberA != null ? numberA : 0
        numberB = numberB != null ? numberB : 1

        return numberA > numberB ? 1 : -1
      } )
      .map( ( station, index ) => {
        const name = Attributes.get( 'name', station ) || station.data.name
        if ( name != null ) {
          return name
        }

        return `Station ${index + 1}`
      } )
    const colorScale = scaleOrdinal( {
      domain : stationNames,
      range : [
        ...stationNames.map( ( item, index ) => {
          return `--color-stationBar${( index % 6 ) + 1}`
        } )
      ],
    } )
    
    const legendColorScale = scaleOrdinal( {
      domain : stationNames,
      range : [
        ...stationNames.map( ( item, index ) => {
          return `var( --color-stationBar${( index % 6 ) + 1}Fill )`
        } )
      ],
    } )

    return { colorScale, legendColorScale, stationNames }
  }, [ stations ] )

  const xMax = width - horizontalMargin
  const yMax = GRAPH_HEIGHT - verticalMargin

  const data = useBarData( {
    data : pData,
    stationNames
  } )

  const xScale = useMemo( () => {
    return scaleBand( {
      // range : [ 0, xMax ],
      // round : true,
      domain : data.map( getHour ),
      padding : 0
    } )
  }, [ data ] )

  const yScale = useMemo( () => {
    return scaleLinear( {
      // range : [ yMax, 0 ],
      // round : true,
      nice : true,
      domain : [ 0, Math.max( ...data.map( getCount ), 1 ) ]
    } )
  }, [ data ] )

  xScale.rangeRound( [ 0, xMax ] )
  yScale.range( [ yMax, 0 ] )

  return (
    <div style={{ position : 'relative' }}>
      <svg width={width} height={GRAPH_HEIGHT}>
        <rect x={0} y={0} width={width} height={GRAPH_HEIGHT} fill={BACKGROUND} rx={14} />
        <Group
          top={verticalMargin / 2}
          left={horizontalMargin / 2}>
          <BarStack
            data={data}
            keys={stationNames}
            x={getHour}
            xScale={xScale}
            yScale={yScale}
            color={colorScale}>
            {barStacks => barStacks.map( barStack => barStack.bars.map( bar => (
              <Bar
                key={`bar-stack-${barStack.index}-${bar.index}`}
                x={bar.x}
                y={bar.y}
                height={bar.height}
                width={bar.width}
                fill={`var( ${bar.color}Fill )`}
                stroke={`var( ${bar.color}Stroke )`}
                onMouseLeave={() => {
                  hideTooltip()
                }}
                onMouseMove={( event ) => {
                  const eventSvgCoords = localPoint( event )

                  showTooltip( {
                    tooltipData : bar,
                    tooltipTop : eventSvgCoords.y - 40,
                    tooltipLeft : eventSvgCoords.x
                  } )
                }} />
            ) ) )}
          </BarStack>
        </Group>
        <AxisLeft
          top={verticalMargin / 2}
          left={horizontalMargin / 2}
          scale={yScale}
          hideTicks
          numTicks={6} />
        <AxisBottom
          top={yMax + verticalMargin / 2}
          left={horizontalMargin / 2}
          tickFormat={formatTime}
          scale={xScale}
          stroke="black"
          hideTicks
          numTicks={numXTicks}
          tickLabelProps={() => ( {
            fill : 'black',
            fontSize : 11,
            textAnchor : 'middle',
          } )}/>
      </svg>
      <div
        style={{
          position : 'absolute',
          top : 0,
          width : '100%',
          display : 'flex',
          justifyContent : 'center',
          fontSize : '14px',
        }}>
        {showLegend && <LegendOrdinal scale={legendColorScale} direction="row" labelMargin="0 15px 0 0" />}
      </div>
      {tooltipData && tooltipOpen && (
        <TooltipWithBounds
          top={tooltipTop}
          left={tooltipLeft}
          style={tooltipStyles}>
          <Text variant="bodyBold">{tooltipData.bar.data.formattedHour}</Text>
          {stationNames.length > 1 && (
            <Flex variant="rowM">
              <Text variant="bodyBold">{`${tooltipData.key}: `}</Text>
              <Text variant="body">{tooltipData.bar.data[tooltipData.key]}</Text>
            </Flex>
          )}
          <Flex variant="rowM">
            <Text variant="bodyBold">Total: </Text>
            <Text variant="body">{tooltipData.bar.data.count}</Text>
          </Flex>
        </TooltipWithBounds>
      )}
    </div>
  )
}

export default withTooltip( UsageBarChart )
